import { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { manualEntriesApi } from '../services/api';
import { toast } from 'sonner';
import { ClipboardCheck, Check, X, Loader2 } from 'lucide-react';
import { useConfirm } from './ConfirmProvider';

const fmtRange = (a, b) => {
  const o = { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' };
  const d = new Date(a).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', weekday: 'short', day: 'numeric', month: 'short' });
  return `${d} · ${new Date(a).toLocaleTimeString('en-AU', o)} → ${new Date(b).toLocaleTimeString('en-AU', o)}`;
};

/** Supervisor queue of worker-submitted manual timesheet entries awaiting approval. */
export function PendingManualEntries({ onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try { const { data } = await manualEntriesApi.list('pending'); setItems(data || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    let reason = '';
    if (!approve) { reason = await confirm({ title: 'Reject these hours?', description: 'The worker is notified and the hours will not be paid.', withInput: true, inputLabel: 'Reason (shown to the worker)', confirmText: 'Reject', destructive: true }); if (reason === null) return; }
    setBusy(id);
    try {
      if (approve) await manualEntriesApi.approve(id); else await manualEntriesApi.reject(id, reason);
      toast.success(approve ? 'Hours approved — now counted in timesheet & payroll' : 'Entry rejected');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  if (items.length === 0) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/40" data-testid="pending-manual-panel">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck className="h-4 w-4 text-amber-700" />
          <h3 className="font-semibold text-amber-900 text-sm">Manual hours awaiting approval</h3>
          <Badge className="bg-amber-600 text-white" data-testid="pending-manual-count">{items.length}</Badge>
        </div>
        <ul className="space-y-2">
          {items.map(e => (
            <li key={e.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg bg-white border border-amber-200 px-3 py-2" data-testid={`pending-manual-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">{e.worker_name} <span className="text-slate-500 font-normal">· {e.total_hours}h{e.break_minutes ? ` (−${e.break_minutes}m break)` : ''}</span></div>
                <div className="text-xs text-slate-600">{fmtRange(e.clock_in, e.clock_out)}{e.client_times?.length ? ` · ${e.client_times.map(c => c.client_name).join(', ')}` : ''}</div>
                <div className="text-xs text-slate-500 italic mt-0.5 truncate" title={e.manual_reason}>“{e.manual_reason}”</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy === e.id} onClick={() => act(e.id, true)} data-testid={`approve-manual-${e.id}`}>
                  {busy === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> Approve</>}
                </Button>
                <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" disabled={busy === e.id} onClick={() => act(e.id, false)} data-testid={`reject-manual-${e.id}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
